"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { parseSseFrames } from "@/lib/chat/parse-sse"
import { localizeSources } from "@/lib/chat/localize-sources"
import { getSessionId } from "@/lib/chat/session-id"
import { useChatSession } from "./use-chat-session"

/** True when the user prefers reduced motion (skip the typewriter reveal). */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
}

/**
 * Lightweight, Chatwoot-free streaming hook for the unified search + AI lane.
 *
 * It posts the same body shape as the full {@link ChatPanel} to `/api/chat`,
 * consumes the SSE stream with the shared {@link parseSseFrames} parser, and
 * accumulates a multi-turn conversation. It deliberately omits nearly every
 * piece of Chatwoot machinery the panel has: no live subscription, no
 * business-hours, crisis routing, email-collect, or Sentry capture.
 *
 * The one exception is {@link UseAiAnswer.handedOff} — it *reports* a handoff
 * without acting on it, because without a live subscription the agent's replies
 * arrive in the full panel and the surface has to be able to say so.
 */
export interface UseAiAnswerArgs {
  scope: string
  country: string
  locale: string
  pageUrl: string
  pageTitle: string
}

export interface AiTurn {
  role: "user" | "bot"
  content: string
  isStreaming?: boolean
  messageId?: string
  /** KB pages the bot cited for this turn (`sources` SSE event). Applied when
   *  the turn finalizes so the row appears with the completed answer, not
   *  mid-reveal. Sanitized server-side (host-allowlisted, deduped, capped). */
  sources?: { label: string; url?: string }[]
}

/** Latest progress phase reported by the backend while the bot works. Cleared
 *  the moment the first answer token arrives (or on done/error). */
export interface AiStatus {
  phase: "understanding" | "retrieving" | "searching"
  index: number | null
  total: number | null
}

export interface UseAiAnswer {
  turns: AiTurn[]
  isStreaming: boolean
  error: boolean
  /** Transient status for the current turn, or null when there's nothing to show. */
  status: AiStatus | null
  /**
   * True once glue reports the conversation is owned by a human (`handoff`, or
   * `handoff_active` for a handover that happened before this page view).
   *
   * This hook still carries no Chatwoot subscription — that's the point of it —
   * so a handed-over conversation's agent replies arrive in the full
   * {@link ChatPanel}, never here. Surfaces MUST use this to say so and offer
   * the way into the panel; silently accepting further messages strands the
   * visitor in a thread they can't see the other half of.
   */
  handedOff: boolean
  /** glue's verdict on whether anyone was reachable when the handoff fired. */
  handoffOnline: boolean | null
  /** `promptKey` marks a chip-click message (trace provenance); absent when typed. */
  ask(message: string, promptKey?: string): Promise<void>
  reset(): void
}

export function useAiAnswer(args: UseAiAnswerArgs): UseAiAnswer {
  const { scope, country, locale, pageUrl, pageTitle } = args

  // Internal session handle for conversation_id / source_id continuity across
  // successive `ask()` calls. Scoped by country + locale like the full panel.
  const { session, setConversation } = useChatSession({ country, locale })

  const [turns, setTurns] = useState<AiTurn[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState(false)
  const [status, setStatus] = useState<AiStatus | null>(null)
  // Handoff is the one piece of Chatwoot state this hook does track: not to act
  // on it, but so the surface can admit the reply is arriving elsewhere.
  const [handedOff, setHandedOff] = useState(false)
  const [handoffOnline, setHandoffOnline] = useState<boolean | null>(null)

  /** Immutably patch the last bot turn (always the final entry while streaming). */
  const patchBotTurn = useCallback((patch: Partial<AiTurn>) => {
    setTurns((prev) => {
      if (prev.length === 0) return prev
      const next = [...prev]
      next[next.length - 1] = { ...next[next.length - 1], ...patch }
      return next
    })
  }, [])

  // ── Typewriter reveal buffer ──
  // The upstream glue delivers the answer in a few sentence-sized bursts that
  // mostly land within one tick, so painting each delta verbatim reads as a
  // single jump rather than streaming. Instead we accumulate the full text in
  // `target` and reveal it toward the user on an animation frame, easing out so
  // small backlogs finish quickly and a big burst catches up smoothly. The
  // network stream finishing (`done`) only finalizes the turn once the reveal
  // has drained — so the typing animation always plays out.
  const targetRef = useRef("")
  const shownRef = useRef(0)
  const doneRef = useRef(false)
  const rafRef = useRef<number | null>(null)
  // KB sources for the current turn, stashed from the `sources` event and
  // applied when the reveal finalizes (so they land with the completed answer).
  const sourcesRef = useRef<AiTurn["sources"] | null>(null)

  const drive = useCallback(() => {
    if (rafRef.current != null) return
    const run = () => {
      const target = targetRef.current
      if (shownRef.current < target.length) {
        const remaining = target.length - shownRef.current
        // Ease-out: reveal ~15% of the backlog per frame, min 2 chars so it
        // never crawls asymptotically toward the tail.
        const step = Math.max(2, Math.ceil(remaining * 0.15))
        shownRef.current = Math.min(target.length, shownRef.current + step)
        patchBotTurn({ content: target.slice(0, shownRef.current) })
      }
      if (shownRef.current >= targetRef.current.length && doneRef.current) {
        rafRef.current = null
        patchBotTurn({
          isStreaming: false,
          ...(sourcesRef.current ? { sources: sourcesRef.current } : {}),
        })
        setIsStreaming(false)
        return
      }
      rafRef.current = requestAnimationFrame(run)
    }
    rafRef.current = requestAnimationFrame(run)
  }, [patchBotTurn])

  // Cancel any in-flight reveal if the component unmounts mid-stream.
  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    },
    []
  )

  const ask = useCallback(
    // `promptKey` marks a message that came from a prompt chip rather than
    // typing (`<scope>.inline.<index>`), so glue can stamp the Langfuse trace
    // with the provenance. Absent for typed messages.
    async (message: string, promptKey?: string) => {
      setError(false)
      setIsStreaming(true)
      setStatus(null)
      setTurns((prev) => [
        ...prev,
        { role: "user", content: message },
        { role: "bot", content: "", isStreaming: true },
      ])

      // Reset the reveal buffer for this turn.
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      targetRef.current = ""
      shownRef.current = 0
      doneRef.current = false
      sourcesRef.current = null
      const smooth = !prefersReducedMotion()

      // A network/transport failure (offline, DNS, connection reset mid-stream,
      // a thrown reader.read()) is an expected user-facing state, not a bug to
      // mask — surface the retry affordance rather than spin forever. The
      // `!res.ok` branch covers HTTP-level failures; the catch covers transport.
      try {
        const res = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: getSessionId(),
            scope,
            country,
            locale,
            page_url: pageUrl,
            page_title: pageTitle,
            entry_point: "inline",
            message,
            prompt_key: promptKey,
            conversation_id: session.conversation_id ?? undefined,
            auth_token: session.source_id ?? undefined,
          }),
        })

        if (!res.ok || !res.body) {
          setError(true)
          setStatus(null)
          patchBotTurn({ isStreaming: false })
          setIsStreaming(false)
          return
        }

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ""

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          const { events, rest, done: sawDone } = parseSseFrames(buffer)
          buffer = rest

          for (const ev of events) {
            if (ev.event === "session") {
              if (
                ev.conversation_id != null &&
                ev.auth_token != null &&
                ev.pubsub_token != null
              ) {
                setConversation({
                  conversation_id: ev.conversation_id,
                  source_id: ev.auth_token,
                  pubsub_token: ev.pubsub_token,
                  email_collect: ev.email_collect,
                })
              }
            } else if (ev.event === "message_meta") {
              if (ev.message_id != null)
                patchBotTurn({ messageId: ev.message_id })
            } else if (ev.event === "sources") {
              // Stash now; applied when the turn finalizes (see drive()).
              // Harden against the active locale (drop cross-locale, re-derive
              // generic labels) before display.
              if (ev.sources?.length)
                sourcesRef.current = localizeSources(ev.sources, locale)
            } else if (ev.event === "progress") {
              if (ev.phase != null) {
                setStatus({
                  phase: ev.phase,
                  index: ev.index ?? null,
                  total: ev.total ?? null,
                })
              }
            } else if (
              ev.event === "handoff" ||
              ev.event === "handoff_active"
            ) {
              // A human now owns this conversation. This hook has no live
              // Chatwoot subscription, so the agent's replies land in the full
              // panel, not here — the surface must say so and offer the way in.
              // `handoff_active` is glue's recovery signal for a conversation
              // that was already handed over before this page view.
              setHandedOff(true)
              if (ev.online != null) setHandoffOnline(ev.online)
            } else if (ev.event === "delta") {
              if (ev.text) {
                // First answer token — the status line has served its purpose.
                setStatus(null)
                targetRef.current += ev.text
                if (smooth) {
                  drive()
                } else {
                  // Reduced motion: paint each delta verbatim, no animation.
                  shownRef.current = targetRef.current.length
                  patchBotTurn({ content: targetRef.current })
                }
              }
            }
          }

          if (sawDone) break
        }

        // Stream finished — no status left to show even if no delta arrived.
        setStatus(null)

        if (smooth) {
          // Let the reveal drain, then it finalizes the turn itself.
          doneRef.current = true
          drive()
        } else {
          patchBotTurn({
            isStreaming: false,
            ...(sourcesRef.current ? { sources: sourcesRef.current } : {}),
          })
          setIsStreaming(false)
        }
      } catch {
        if (rafRef.current != null) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
        setError(true)
        setStatus(null)
        patchBotTurn({ isStreaming: false })
        setIsStreaming(false)
      }
    },
    [
      scope,
      country,
      locale,
      pageUrl,
      pageTitle,
      session.conversation_id,
      session.source_id,
      setConversation,
      patchBotTurn,
      drive,
    ]
  )

  const reset = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    targetRef.current = ""
    shownRef.current = 0
    doneRef.current = false
    setTurns([])
    setError(false)
    setIsStreaming(false)
    setStatus(null)
    setHandedOff(false)
    setHandoffOnline(null)
  }, [])

  return {
    turns,
    isStreaming,
    error,
    status,
    handedOff,
    handoffOnline,
    ask,
    reset,
  }
}
